import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ShieldCheck, Upload, Plus, X, Loader2, Check, AlertTriangle, FileText,
  BadgeCheck, ScrollText, Scale, Lock, ArrowRight, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_PALETTE = {
  pending: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', label: 'Under Admin Review' },
  verified: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', label: 'Verified' },
  rejected: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', label: 'Rejected — resubmit' },
  suspended: { bg: 'bg-slate-100', border: 'border-slate-400', text: 'text-slate-700', label: 'Suspended' },
};

const DEFAULT_FEE = { service: '', fee: '', currency: 'GBP', description: '' };

const ProfessionalOnboardingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [existingProfile, setExistingProfile] = useState(null);

  // Wizard state
  const [step, setStep] = useState(1);
  const [types, setTypes] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [nda, setNda] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    country: user?.country || 'UK',
    professional_type: '',
    license_body: '',
    license_number: '',
    license_state: '',
    id_doc_url: '',
    license_doc_url: '',
    bio: '',
    fee_schedule: [{ ...DEFAULT_FEE }],
    nda_signature_name: '',
    conduct_accepted: false,
    indemnity_accepted: false,
    policy_accepted: false,
  });

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Sync form.country from AuthContext once user loads
  useEffect(() => {
    if (user?.country && form.country === 'UK' && user.country !== 'UK') {
      setForm(prev => ({ ...prev, country: user.country }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.country]);

  const fetchAll = useCallback(async () => {
    try {
      const [meRes, policyRes, ndaRes] = await Promise.all([
        axios.get(`${API_URL}/api/professional-onboarding/me`, { headers: authHeaders }),
        axios.get(`${API_URL}/api/professional-onboarding/policy`, { params: { country: form.country } }),
        axios.get(`${API_URL}/api/professional-onboarding/nda`),
      ]);
      setExistingProfile(meRes.data.profile);
      setPolicy(policyRes.data);
      setNda(ndaRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Professional role required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.country]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!form.country) return;
    axios.get(`${API_URL}/api/professional-onboarding/types`, { params: { country: form.country } })
      .then(r => setTypes(r.data.types || []))
      .catch(() => setTypes([]));
  }, [form.country]);

  const selectedType = types.find(t => t.key === form.professional_type);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const uploadDocument = async (file, field) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error('File too large — max 15MB'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/upload/kyp-document`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      setField(field, res.data.url);
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const addFee = () => setField('fee_schedule', [...form.fee_schedule, { ...DEFAULT_FEE }]);
  const updateFee = (idx, patch) => setField('fee_schedule', form.fee_schedule.map((f, i) => i === idx ? { ...f, ...patch } : f));
  const removeFee = (idx) => setField('fee_schedule', form.fee_schedule.filter((_, i) => i !== idx));

  const canAdvance = () => {
    if (step === 1) return !!form.professional_type;
    if (step === 2) return form.license_number.trim() && form.id_doc_url && form.license_doc_url;
    if (step === 3) return form.fee_schedule.every(f => f.service.trim() && f.fee);
    if (step === 4) return form.policy_accepted && form.conduct_accepted && form.indemnity_accepted && form.nda_signature_name.trim();
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        license_body: selectedType?.body || form.license_body,
        fee_schedule: form.fee_schedule.map(f => ({ ...f, fee: parseFloat(f.fee) || 0 })),
      };
      await axios.post(`${API_URL}/api/professional-onboarding/submit`, payload, { headers: authHeaders });
      toast.success('KYP submitted — awaiting admin review');
      setStep(6);
      // Defer showing the persistent status-card until user clicks "Return to Dashboard"
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E07A3A]" /></div>;
  }

  // Already submitted — show status card instead of re-submission
  const statusColor = existingProfile ? STATUS_PALETTE[existingProfile.status] || STATUS_PALETTE.pending : null;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="font-['Playfair_Display'] text-4xl text-[#1A1A1A] mb-2" data-testid="kyp-heading">Professional Onboarding</h1>
          <p className="text-slate-500 text-base">Know-Your-Professional (KYP) verification keeps Shopperden and its users safe. Every professional completes this once.</p>
        </div>

        {/* Existing profile status */}
        {existingProfile && (
          <div className={`${statusColor.bg} ${statusColor.border} border p-5 mb-8 flex items-start gap-4`} data-testid="kyp-status-card">
            <ShieldCheck className={`${statusColor.text} h-6 w-6 mt-0.5 flex-shrink-0`} />
            <div className="flex-1">
              <p className={`${statusColor.text} font-semibold text-lg`}>Status: {statusColor.label}</p>
              <p className="text-sm text-slate-600 mt-1">
                Submitted: <span className="font-medium">{existingProfile.submitted_at?.slice(0, 10)}</span> &middot;{' '}
                Type: <span className="font-medium">{existingProfile.professional_type}</span> &middot;{' '}
                Licence: <span className="font-medium">{existingProfile.license_number}</span>
              </p>
              {existingProfile.status === 'rejected' && existingProfile.rejection_reason && (
                <div className="mt-3 bg-red-100 border border-red-200 p-3 text-sm text-red-900">
                  <p className="font-semibold">Reason: {existingProfile.rejection_reason}</p>
                  <button type="button" onClick={() => setExistingProfile(null)} className="mt-2 text-red-700 underline text-xs font-semibold"
                    data-testid="resubmit-kyp-btn">Resubmit corrected details →</button>
                </div>
              )}
              {existingProfile.status === 'verified' && (
                <Button type="button" onClick={() => navigate('/professional')} className="mt-3 bg-[#0F172A] text-white hover:bg-[#1e293b] h-9 text-xs"
                  data-testid="go-to-dashboard-btn">Go to Dashboard</Button>
              )}
            </div>
          </div>
        )}

        {!existingProfile && (
          <>
            {/* Progress */}
            <div className="flex items-center gap-2 mb-8" data-testid="kyp-progress">
              {[
                { n: 1, label: 'Type' },
                { n: 2, label: 'Credentials' },
                { n: 3, label: 'Fees' },
                { n: 4, label: 'NDA & Conduct' },
                { n: 5, label: 'Review' },
              ].map(s => (
                <div key={s.n} className="flex-1 flex items-center gap-2">
                  <div className={`w-8 h-8 flex items-center justify-center font-semibold text-sm border ${step >= s.n ? 'bg-[#E07A3A] border-[#E07A3A] text-white' : 'bg-white border-slate-200 text-slate-400'}`}>
                    {step > s.n ? <Check className="h-4 w-4" /> : s.n}
                  </div>
                  <span className={`text-xs font-medium hidden sm:inline ${step >= s.n ? 'text-[#1A1A1A]' : 'text-slate-400'}`}>{s.label}</span>
                  {s.n < 5 && <div className={`flex-1 h-px ${step > s.n ? 'bg-[#E07A3A]' : 'bg-slate-200'}`} />}
                </div>
              ))}
            </div>

            <div className="luxury-card p-6 md:p-8">
              {/* STEP 1: Type */}
              {step === 1 && (
                <div className="space-y-5" data-testid="kyp-step-1">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-1 font-semibold">1. Select Your Professional Type</h2>
                    <p className="text-slate-500 text-sm">Available types are based on your country's regulatory framework.</p>
                  </div>
                  <div>
                    <Label className="text-xs tracking-wider uppercase font-semibold text-slate-500">Country</Label>
                    <Select value={form.country} onValueChange={(v) => setField('country', v)}>
                      <SelectTrigger className="mt-1 bg-[#F8FAFC] border-slate-200 h-12" data-testid="kyp-country-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="UK">United Kingdom</SelectItem>
                        <SelectItem value="IN">India</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                        <SelectItem value="US">United States</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-wider uppercase font-semibold text-slate-500">Professional Type</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {types.map(t => (
                        <button key={t.key} type="button" onClick={() => setField('professional_type', t.key)}
                          className={`text-left p-4 border transition-colors ${form.professional_type === t.key ? 'border-[#E07A3A] bg-[#FEF3EC]' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                          data-testid={`kyp-type-${t.key}`}>
                          <p className="font-semibold text-[#1A1A1A] text-sm">{t.label}</p>
                          <p className="text-xs text-slate-500 mt-1">{t.body}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Credentials */}
              {step === 2 && selectedType && (
                <div className="space-y-5" data-testid="kyp-step-2">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-1 font-semibold">2. Credentials & Documents</h2>
                    <p className="text-slate-500 text-sm">{selectedType.body} — {selectedType.verify_hint}</p>
                  </div>

                  <div>
                    <Label className="text-xs tracking-wider uppercase font-semibold text-slate-500">Licence / Registration Number</Label>
                    <Input value={form.license_number} onChange={(e) => setField('license_number', e.target.value)}
                      placeholder={selectedType.placeholder} className="mt-1 bg-[#F8FAFC] border-slate-200 h-12" data-testid="kyp-license-number" />
                  </div>

                  {form.country === 'IN' || form.country === 'CA' || form.country === 'US' ? (
                    <div>
                      <Label className="text-xs tracking-wider uppercase font-semibold text-slate-500">State / Province (if applicable)</Label>
                      <Input value={form.license_state} onChange={(e) => setField('license_state', e.target.value)}
                        placeholder="e.g. Punjab / Ontario / California" className="mt-1 bg-[#F8FAFC] border-slate-200 h-12" data-testid="kyp-license-state" />
                    </div>
                  ) : null}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <DocUpload label="Government-issued ID" field="id_doc_url" value={form.id_doc_url} onPick={uploadDocument} testId="kyp-upload-id" />
                    <DocUpload label="Licence / Registration Certificate" field="license_doc_url" value={form.license_doc_url} onPick={uploadDocument} testId="kyp-upload-license" />
                  </div>

                  <div>
                    <Label className="text-xs tracking-wider uppercase font-semibold text-slate-500">Short Bio (optional)</Label>
                    <Textarea value={form.bio} onChange={(e) => setField('bio', e.target.value)} rows={3}
                      placeholder="Years of experience, specialities, languages spoken…" className="mt-1 bg-[#F8FAFC] border-slate-200" data-testid="kyp-bio" />
                  </div>
                </div>
              )}

              {/* STEP 3: Fees */}
              {step === 3 && (
                <div className="space-y-5" data-testid="kyp-step-3">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-1 font-semibold">3. Fee Schedule (Transparency)</h2>
                    <p className="text-slate-500 text-sm">Professional ethics rules (Bar Council / ICAI / SRA / RICS) require fees to be clearly disclosed. Shopperden charges a flat Platform Access Fee and takes no commission on your consultations.</p>
                  </div>

                  {form.fee_schedule.map((fee, idx) => (
                    <div key={`fee-${idx}`} className="grid sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end" data-testid={`fee-row-${idx}`}>
                      <div>
                        <Label className="text-[10px] tracking-wider uppercase font-semibold text-slate-500">Service</Label>
                        <Input value={fee.service} onChange={(e) => updateFee(idx, { service: e.target.value })}
                          placeholder="e.g. Title Verification" className="mt-1 bg-[#F8FAFC] border-slate-200 h-11" />
                      </div>
                      <div>
                        <Label className="text-[10px] tracking-wider uppercase font-semibold text-slate-500">Fee</Label>
                        <Input type="number" value={fee.fee} onChange={(e) => updateFee(idx, { fee: e.target.value })}
                          placeholder="0" className="mt-1 bg-[#F8FAFC] border-slate-200 h-11" />
                      </div>
                      <div>
                        <Label className="text-[10px] tracking-wider uppercase font-semibold text-slate-500">Currency</Label>
                        <Select value={fee.currency} onValueChange={(v) => updateFee(idx, { currency: v })}>
                          <SelectTrigger className="mt-1 bg-[#F8FAFC] border-slate-200 h-11"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {['GBP', 'INR', 'CAD', 'USD', 'EUR', 'AED', 'SGD', 'AUD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <button type="button" onClick={() => removeFee(idx)} disabled={form.fee_schedule.length === 1}
                        className="h-11 px-3 text-slate-400 hover:text-red-500 disabled:opacity-30">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addFee} className="border-[#E07A3A] text-[#E07A3A] hover:bg-[#FEF3EC] h-10 text-xs" data-testid="add-fee-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add another service
                  </Button>
                </div>
              )}

              {/* STEP 4: NDA + Conduct */}
              {step === 4 && policy && nda && (
                <div className="space-y-5" data-testid="kyp-step-4">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-1 font-semibold">4. NDA, Conduct & Indemnity</h2>
                    <p className="text-slate-500 text-sm">Read and electronically sign the three documents below. Your IP and timestamp are recorded.</p>
                  </div>

                  {/* Policy */}
                  <details className="border border-slate-200 bg-[#F8FAFC] p-4 open:bg-white" open>
                    <summary className="cursor-pointer font-semibold text-sm flex items-center gap-2"><ScrollText className="h-4 w-4 text-[#E07A3A]" />Professional Conduct Policy v{policy.version}</summary>
                    <div className="mt-3 max-h-48 overflow-y-auto text-xs space-y-3 text-slate-600 pr-2">
                      {policy.sections.map(s => <div key={s.id}><p className="font-semibold text-[#1A1A1A] mb-1">{s.title}</p><p>{s.body}</p></div>)}
                      <p className="italic text-slate-500">{policy.country_note}</p>
                    </div>
                    <label className="flex items-center gap-2 mt-3 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={form.policy_accepted} onChange={(e) => setField('policy_accepted', e.target.checked)} data-testid="policy-accept-checkbox" />
                      I have read and agree to the Professional Conduct Policy
                    </label>
                  </details>

                  {/* Conduct + Indemnity quick checks */}
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer p-3 border border-slate-200 bg-white">
                      <input type="checkbox" checked={form.conduct_accepted} onChange={(e) => setField('conduct_accepted', e.target.checked)} className="mt-0.5" data-testid="conduct-accept-checkbox" />
                      <span>I understand I must NOT cold-call or solicit users outside the platform, and I must disclose all fees transparently.</span>
                    </label>
                    <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer p-3 border border-slate-200 bg-white">
                      <input type="checkbox" checked={form.indemnity_accepted} onChange={(e) => setField('indemnity_accepted', e.target.checked)} className="mt-0.5" data-testid="indemnity-accept-checkbox" />
                      <span>I agree to indemnify Shopperden against any claim arising from my negligent, wrong or unethical advice.</span>
                    </label>
                  </div>

                  {/* NDA */}
                  <div className="border-2 border-[#E07A3A]/40 bg-[#FEF3EC]/30 p-4">
                    <div className="flex items-center gap-2 mb-2"><Lock className="h-4 w-4 text-[#E07A3A]" /><p className="font-semibold text-sm text-[#1A1A1A]">{nda.title} v{nda.version}</p></div>
                    <div className="max-h-56 overflow-y-auto text-xs text-slate-700 whitespace-pre-line bg-white border border-slate-200 p-3 mb-3 leading-relaxed" data-testid="nda-body">{nda.body}</div>
                    <Label className="text-xs tracking-wider uppercase font-semibold text-slate-500">Type your full legal name to sign</Label>
                    <Input value={form.nda_signature_name} onChange={(e) => setField('nda_signature_name', e.target.value)}
                      placeholder="e.g. Jane Smith" className="mt-1 bg-white border-slate-300 h-11 font-serif italic" data-testid="nda-signature-input" />
                    <p className="text-[10px] text-slate-400 mt-1">Your IP address and current timestamp will be recorded with this signature.</p>
                  </div>
                </div>
              )}

              {/* STEP 5: Review */}
              {step === 5 && selectedType && (
                <div className="space-y-4" data-testid="kyp-step-5">
                  <div>
                    <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-1 font-semibold">5. Review & Submit</h2>
                    <p className="text-slate-500 text-sm">Please verify everything below before submitting to admin review.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <ReviewRow icon={<BadgeCheck />} label="Type" value={selectedType.label} />
                    <ReviewRow icon={<BadgeCheck />} label="Regulator" value={selectedType.body} />
                    <ReviewRow icon={<FileText />} label="Licence #" value={form.license_number} />
                    <ReviewRow icon={<FileText />} label="Country" value={form.country} />
                    <ReviewRow icon={<FileText />} label="ID Doc" value={form.id_doc_url ? '✔ uploaded' : '—'} />
                    <ReviewRow icon={<FileText />} label="Licence Doc" value={form.license_doc_url ? '✔ uploaded' : '—'} />
                    <ReviewRow icon={<Scale />} label="Services" value={`${form.fee_schedule.length} listed`} />
                    <ReviewRow icon={<ShieldCheck />} label="NDA Signed" value={form.nda_signature_name || '—'} />
                  </div>
                </div>
              )}

              {/* STEP 6: Done */}
              {step === 6 && (
                <div className="text-center py-12" data-testid="kyp-step-6">
                  <div className="w-16 h-16 rounded-full bg-green-50 border-2 border-green-300 flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Submitted — awaiting admin review</h2>
                  <p className="text-slate-500 text-sm mb-5">You'll receive a notification as soon as your verification is complete. This usually takes 1–2 business days.</p>
                  <Button onClick={() => navigate('/professional')} className="bg-[#0F172A] text-white hover:bg-[#1e293b]">Return to Dashboard</Button>
                </div>
              )}

              {/* Nav buttons */}
              {step < 6 && (
                <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
                  <Button type="button" variant="outline" onClick={() => setStep(step - 1)} disabled={step === 1}
                    className="border-slate-200 text-slate-600 h-10 text-xs" data-testid="kyp-back-btn">
                    <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
                  </Button>
                  {step < 5 ? (
                    <Button type="button" onClick={() => setStep(step + 1)} disabled={!canAdvance()}
                      className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-10 text-xs" data-testid="kyp-next-btn">
                      Next <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  ) : (
                    <Button type="button" onClick={submit} disabled={submitting}
                      className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-10 text-xs" data-testid="kyp-submit-btn">
                      {submitting ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Submitting…</> : <>Submit for review <ShieldCheck className="h-3.5 w-3.5 ml-1.5" /></>}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Warning */}
            <div className="mt-6 flex items-start gap-2 bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
              <span>Providing false, expired or impersonated credentials results in immediate and permanent de-listing. Shopperden may verify your licence directly with the issuing body.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// --- Small sub-components (kept inside file for locality) ---

const DocUpload = ({ label, field, value, onPick, testId }) => {
  const inputRef = React.useRef(null);
  return (
    <div>
      <Label className="text-xs tracking-wider uppercase font-semibold text-slate-500">{label}</Label>
      <input ref={inputRef} type="file" accept="image/*,.pdf" className="hidden"
        onChange={(e) => onPick(e.target.files?.[0], field)} data-testid={testId} />
      <button type="button" onClick={() => inputRef.current?.click()}
        className="mt-1 w-full border-2 border-dashed border-slate-200 hover:border-[#E07A3A] p-4 flex flex-col items-center gap-1 transition-colors bg-[#F8FAFC]">
        {value ? <>
          <Check className="h-5 w-5 text-green-600" />
          <span className="text-xs text-green-700 font-medium">Uploaded — click to replace</span>
        </> : <>
          <Upload className="h-5 w-5 text-[#E07A3A]" />
          <span className="text-xs text-slate-500">Upload (image or PDF)</span>
        </>}
      </button>
    </div>
  );
};

const ReviewRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 p-3">
    <span className="text-[#E07A3A] [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
    <span className="text-xs text-slate-500 uppercase tracking-wider">{label}:</span>
    <span className="text-sm text-[#1A1A1A] font-medium ml-auto truncate">{value}</span>
  </div>
);

export default ProfessionalOnboardingPage;
